import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CreateClientTest } from 'api/Clients/Types/CreateClientTest'
import { TestAnswerDto } from 'api/Clients/Types/TestDto'
import { clientTestTypes } from 'api/Clients/Types/TestQuestionDto'
import { TestSectionsDto } from 'api/Clients/Types/TestSectionsDto'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { canEditEconomicProfileTestForClientStatus } from 'utils/clients.helpers'

import { ClientTestForm } from './ClientTestForm'
import { ClientDetailsNoMatch } from './NotFoundTest'

const ClientTestFormWindow = (): JSX.Element => {
  const { type, clientId, testId } = useParams<{ type: string; clientId: string; testId: string }>()
  const location = useLocation()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const isCorporateQuery = new URLSearchParams(location.search).get('isCorporate')

  const isCorporate = useMemo(() => isCorporateQuery === 'true', [isCorporateQuery])

  const [isTestBlocked, setTestBlocked] = useState<boolean>(false)
  const [selectedTestAnswer, setSelectedTestAnswer] = useState<TestAnswerDto | null>(null)
  const [questions, setQuestions] = useState<TestSectionsDto | null>(null)

  const { callback } = useCallbackWithForceRefresh(() => {
    return clientId ? clientsApiClient.getClient(clientId) : Promise.resolve(null)
  }, [clientId])
  const { data: clientData } = useFetchOne(callback)

  const isEconomicProfileBlockedByStatus =
    type === clientTestTypes.economicProfile &&
    !!clientData &&
    !canEditEconomicProfileTestForClientStatus(clientData.status.id)

  useEffect(() => {
    if (testId) setTestAnswer(testId)
  }, [testId])

  const setTestAnswer = async (testId: string) => {
    const clientTestAnswer = await clientsApiClient.getClientTestAnswer(testId)
    setSelectedTestAnswer(clientTestAnswer)
  }

  useEffect(() => {
    if (clientData) {
      if (isEconomicProfileBlockedByStatus) {
        setTestBlocked(true)
        return
      }

      setTestBlocked(false)

      if (selectedTestAnswer) {
        fillTestQuestions({
          tickmillCompanyId: clientData.tickmillCompany.id,
          version: selectedTestAnswer.version,
        })
      } else {
        fillTestQuestions({
          tickmillCompanyId: clientData.tickmillCompany.id,
        })
      }
    }
  }, [clientData, isEconomicProfileBlockedByStatus, selectedTestAnswer])

  const fillTestQuestions = async ({
    tickmillCompanyId,
    version,
  }: {
    tickmillCompanyId: number
    version?: number
  }) => {
    const subcategoryId = new URLSearchParams(location.search).get('subcategoryId') || ''
    const productId = new URLSearchParams(location.search).get('productId') || ''

    if ((type === clientTestTypes.client || type === clientTestTypes.economicProfile) && testId) {
      const testFetch = await clientsApiClient.getClientAppTestQuestions({
        clientTestId: testId,
        languageId: 'en',
      })
      setQuestions(testFetch)
    } else if (type === clientTestTypes.client || type === clientTestTypes.economicProfile) {
      const testFetch = await clientsApiClient.getClientAppTestQuestionsForTickmillCompany({
        tickmillCompanyId,
        version,
        subcategoryId,
        productId,
      })
      setQuestions(testFetch)
    } else if (type === clientTestTypes.ib) {
      const testFetch = await clientsApiClient.getClientIbInfoTestQuestionsForTickmillCompany(
        tickmillCompanyId,
        version
      )
      setQuestions(testFetch)
    } else if (type === clientTestTypes.kyc) {
      const testFetch = await clientsApiClient.getClientKYCTestQuestions(tickmillCompanyId)
      setQuestions(testFetch)
    } else if (type === clientTestTypes.classification) {
      const testFetch =
        await clientsApiClient.getClientClassificationTestQuestionsForTickmillCompany(
          tickmillCompanyId,
          version,
          subcategoryId
        )
      setQuestions(testFetch)
    } else if (type === clientTestTypes.w8beneficial) {
      const testFetch = await clientsApiClient.getClientTaxTestQuestionsForTickmillCompany(
        tickmillCompanyId,
        version,
        subcategoryId
      )
      setQuestions(testFetch)
    } else if (type === clientTestTypes.corporate) {
      const testFetch = await clientsApiClient.getClientCorporateTestQuestions(
        tickmillCompanyId,
        subcategoryId
      )
      setQuestions(testFetch)
    } else {
      setTestBlocked(true)
    }
  }

  const handleSubmit = async (values: CreateClientTest) => {
    await clientsApiClient.createClientTest(values)
    window.close()
  }

  return (
    <>
      {questions && !isTestBlocked && (
        <ClientTestForm
          clientId={clientId ?? ''}
          test={questions}
          fromExistingTest={selectedTestAnswer}
          onSubmit={handleSubmit}
          isCorporate={isCorporate}
        />
      )}
      {isTestBlocked && (
        <ClientDetailsNoMatch
          header={
            isEconomicProfileBlockedByStatus
              ? 'Economic Profile changes cannot be updated/created for clients with Activated, Dormant, Closed, Blocked, or Validation Denied status.'
              : 'For this entity test cannot be added.'
          }
          onClick={() => window.close()}
        />
      )}
    </>
  )
}

export default ClientTestFormWindow
